/**
 * Copyright (C) 2026 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { reorder } from '../../../../utils/DragAndDropUtils';
import { ChartMetadataList } from './ChartItems';
import { StyledChartContainerBuffer } from './ChartsContainerWrapper';

interface ChartsListProps {
  chartList: ChartMetadataList;
  onChartListChange: (chartList: ChartMetadataList) => void;
  children?: React.ReactNode;
}

function ChartsList({ chartList, onChartListChange, children }: ChartsListProps) {
  const [isDragging, setIsDragging] = useState<boolean>(false);

  function onDragStart() {
    setIsDragging(true);
  }

  function onDragEnd(result: any) {
    const { source, destination } = result;
    setIsDragging(false);

    // dropped outside the list, so ignore any changes
    if (!destination) {
      return;
    }

    // reorder the charts in the state
    onChartListChange(reorder(chartList, source.index, destination.index));
  }

  return (
    <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
      <Droppable droppableId="charts__droppable">
        {(provided: any, snapshot: any) => (
          <div ref={provided.innerRef} className="ftv__charts__droppable">
            {Object.keys(chartList).map((chartKey: string, idx: number) => {
              const chartMetadata = chartList[chartKey];

              return (
                <chartMetadata.component
                  key={chartKey}
                  text={chartMetadata.text}
                  id={chartKey}
                  index={idx}
                  onlyUseSecondaryTerminalHistory={chartMetadata.onlyUseSecondaryTerminalHistory}
                  onlyUsePrimaryTerminalHistory={chartMetadata.onlyUsePrimaryTerminalHistory}
                  showKuOrKaChartNamePrefix={chartMetadata.showKuOrKaChartNamePrefix}
                  isRestricted={chartMetadata.isRestricted}
                  alwaysShow={chartMetadata.alwaysShow}
                  acceptedTerminalTypes={chartMetadata.acceptedTerminalTypes}
                  acceptedPlatformTypes={chartMetadata.acceptedPlatformTypes}
                  acceptedNetworkTypes={chartMetadata.acceptedNetworkTypes}
                  useSelectedKuPlusKaTabTerminalHistory={chartMetadata.useSelectedKuPlusKaTabTerminalHistory}
                />
              );
            })}
            {
              isDragging ? <StyledChartContainerBuffer /> : null // fixes glitchy bug when dragging to bottom of list
            }
            {provided.placeholder}
            {children}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default ChartsList;
